'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import {
    Layers,
    Infinity,
    CreditCard,
    Languages,
    BadgeCheck,
    Wrench,
    Network,
    ShieldCheck,
} from 'lucide-react';

export function FeaturesHighlightsSection() {
    const t = useTranslations('features');

    const highlights = [
        { icon: Layers, text: t('highlights.hostOptions') },
        { icon: Infinity, text: t('highlights.unlimitedPlans') },
        { icon: CreditCard, text: t('highlights.paymentOptions') },
        { icon: Languages, text: t('highlights.multilingual') },
        { icon: BadgeCheck, text: t('highlights.whiteLabel') },
        { icon: Wrench, text: t('highlights.topServices') },
        { icon: Network, text: t('highlights.manageNetwork') },
        { icon: ShieldCheck, text: t('highlights.secureAuth') },
    ];

    return (
        <SectionWrapper
            variant="default"
            padding="lg"
            className="relative overflow-hidden"
        >
            <div className="pointer-events-none absolute inset-0 opacity-50">
                <div className="absolute -left-16 top-16 h-64 w-64 rounded-full bg-primary-500/8 blur-3xl" />
                <div className="absolute -right-24 bottom-10 h-72 w-72 rounded-full bg-blue-500/8 blur-3xl" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto">
                <div className="space-y-4 max-w-4xl">
                    <Typography
                        variant="h2"
                        className="!text-2xl font-bold text-gray-900 dark:text-white tracking-tight"
                    >
                        {t('highlights.title')}
                    </Typography>
                    <Typography
                        variant="p"
                        className="text-base md:text-lg text-gray-600 dark:text-gray-300 leading-relaxed"
                    >
                        {t('highlights.subtitle')}
                    </Typography>
                </div>

                <ul className="mt-8 grid gap-4 md:gap-5 md:grid-cols-2">
                    {highlights.map((item, index) => {
                        const Icon = item.icon;
                        return (
                            <li
                                key={index}
                                className="group relative overflow-hidden rounded-2xl border border-gray-200/80 dark:border-gray-800/80 bg-white/80 dark:bg-gray-900/80 px-4 py-4 md:px-5 md:py-5 shadow-sm hover:shadow-lg hover:border-primary-500/60 transition-all duration-300"
                            >
                                <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-primary-500/5 via-transparent to-blue-500/5" />
                                <div className="relative flex items-center gap-3">
                                    <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary-50 dark:bg-primary-900/20 border border-primary-100/80 dark:border-primary-700/60 shadow-sm">
                                        <Icon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                                    </div>
                                    <p className="text-sm md:text-base text-gray-700 dark:text-gray-200 leading-relaxed">
                                        {item.text}
                                    </p>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </SectionWrapper>
    );
}

